import React, { useState, createContext, useContext, useMemo } from 'react';
import { ToastContainer, ToastMessage } from '@teko/ui';
import { useGlobalData } from './GlobalDataContext';

interface ToastProps {
  id?: number;
  content?: React.ReactNode;
  animation?: 'fade' | 'slide';
  duration?: number;
  position?: 'center-center' | 'bottom-center' | 'top-left';
}
interface ContextValue {
  showToast: (_: React.ReactNode, options?: ToastProps) => void;
}

const defaultValues: ContextValue = {
  showToast: null,
};

const ToastPositions = ['center-center', 'bottom-center', 'top-left'];

interface ToastProviderProps {
  children?: any;
}

const MAX_LENGTH = 5;

const ToastContext = createContext(defaultValues);

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
}: ToastProviderProps) => {
  const [toast, setToast] = useState<ToastProps[]>([]);
  const { isMobile } = useGlobalData();

  const sortedToast = useMemo(
    () =>
      toast.reduce((total: any, current: ToastProps) => {
        const currentPosition = current.hasOwnProperty('position')
          ? current['position']
          : isMobile
          ? 'bottom-center'
          : 'top-left';
        return {
          ...total,
          [currentPosition]: total[currentPosition]
            ? [...total[currentPosition], current]
            : [current],
        };
      }, {}),
    [toast, isMobile],
  );

  const showToast = (content: React.ReactNode, options?: ToastProps) => {
    if (toast.length >= MAX_LENGTH) return;
    const id = new Date().getTime() + Math.random();
    setToast((prev) =>
      prev.length >= MAX_LENGTH ? prev : [...prev, { content, id, ...options }],
    );
  };

  const handleRemove = (id: number) => {
    setToast((prev) => prev.filter((t: ToastProps) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {typeof window !== 'undefined' && (
        <>
          {ToastPositions.filter((position) =>
            sortedToast.hasOwnProperty(position),
          ).map((position, idx) => (
            <ToastContainer position={position} key={idx}>
              {sortedToast[position].map(
                ({ content, id, animation, duration }: any) => (
                  <ToastMessage
                    key={id}
                    onRemove={() => handleRemove(id)}
                    animation={
                      animation ? animation : isMobile ? 'fade' : 'slide'
                    }
                    countdown={duration}
                  >
                    {content}
                  </ToastMessage>
                ),
              )}
            </ToastContainer>
          ))}
        </>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error(
      'useToast must be inside a ToastProvider with a showToast value',
    );
  }
  return context;
};
